import { BoxContainer, BoxContainerProps, CircleIcon, FluidCanvas, PrimaryButton, ReactIcons } from "@kalyzee/kalyzee-ui";

import workBoxStyles from "./WorkBox.module.css";
import styles from "./PlayerWorkBox.module.css";
import React from "react";
import clsx from "clsx";
import { UserAvatar } from "@kalyzee/kast-web-sdk/web-components";

/**
 * Round action button — used both in the topbar (always visible) and as a
 * centered overlay on hover. Same shape, different render slot.
 */
export interface PlayerActionItem {
  icon: React.ReactNode;
  onClick: () => void;
  label?: string;
  /** Optional key to stabilize React reconciliation (defaults to index). */
  key?: string;
}

/** @deprecated alias kept for compat — prefer {@link PlayerActionItem}. */
export type PlayerTopbarAction = PlayerActionItem;

export interface PlayerWorkBoxProps {
  children: React.ReactNode;
  label: string;
  labelInfo?: string;
  userDisplayName?: string;
  avatar?: string;
  overlayMode?: BoxContainerProps["mode"];
  /**
   * Free-form overlay content (e.g. PTZ controls). Renders inside the
   * topbar's overlay layer below the topbar itself.
   */
  overlayContent?: React.ReactNode;
  /**
   * Round action buttons appended to the topbar's right slot, after the
   * existing camera/mic media buttons.
   */
  topbarActions?: PlayerActionItem[];
  /**
   * Round action buttons rendered as a centered overlay on top of the player
   * content. Stacked on top of any custom `overlayContent`.
   */
  overlayActions?: PlayerActionItem[];
  extraHeaderContent?: React.ReactNode;
  isScreenSource?: boolean;
  className?: string;
  topBarClassName?: string;
  audio?: boolean;
  video?: boolean;
  isMe?: boolean;
  isHighlighted?: boolean;
  isPlayingSound?: boolean;
  isSmall?: boolean;
  boxProps?: React.HTMLAttributes<HTMLDivElement>;
  idleAnimation?: boolean;
  loading?: boolean;
  mediaButtons?: boolean;
  localStream?: boolean;
  onChangeAudio?: () => void;
  onChangeVideo?: () => void;
}


export const PlayerWorkBox = React.memo(({
  children,
  label,
  labelInfo,
  userDisplayName,
  avatar,
  overlayMode,
  overlayContent,
  topbarActions,
  overlayActions,
  extraHeaderContent,
  isScreenSource,
  className,
  topBarClassName,
  audio,
  video,
  isMe,
  isHighlighted,
  isPlayingSound,
  isSmall,
  boxProps,
  idleAnimation,
  loading,
  mediaButtons,
  localStream,
  onChangeAudio,
  onChangeVideo,
}: PlayerWorkBoxProps) => {
  const left = React.useMemo(() => {
    const result: React.ReactNode[] = [];
    if (extraHeaderContent) {
      result.push(extraHeaderContent);
    }
    if (isHighlighted) {
      result.push(<ReactIcons.Hand2 className={styles.nameIcon} />);
    } else if (isMe) {
      result.push(<ReactIcons.Home className={styles.nameIcon} />);
    }
    result.push(<span key="label" className={workBoxStyles.label}>{label}</span>);
    if (labelInfo) {
      result.push(
        <span key="youLabel" className={workBoxStyles.labelInfo}>{labelInfo}</span>
      );
    }
    if (isScreenSource && !isSmall) {
      result.push(
        <span key="screen-badge" className={styles.screenBadge}>
          <ReactIcons.ScreenShare />
          {'Écran'}
        </span>
      );
    }
    return result;
  }, [extraHeaderContent, isHighlighted, isMe, isScreenSource, isSmall, label, labelInfo]);

  const right = React.useMemo(() => {
    const result: React.ReactNode[] = [];
    if (video !== undefined && mediaButtons) {
      result.push(
        <PrimaryButton
          key="video"
          onClick={onChangeVideo}
        >
          <CircleIcon.Variants.Camera
            className={styles.mediaBtn}
            disabled={!video}
          />
        </PrimaryButton>
      );
    }
    if (audio !== undefined && mediaButtons) {
      result.push(
        <PrimaryButton
          key="audio"
          onClick={onChangeAudio}
        >
          <CircleIcon.Variants.Mic
            className={styles.mediaBtn}
            disabled={!audio}
          />
        </PrimaryButton>
      );
    }
    topbarActions?.forEach((action, idx) => {
      result.push(
        <PrimaryButton
          key={action.key ?? `topbar-action-${idx}`}
          onClick={action.onClick}
          nativeProps={action.label ? { "aria-label": action.label, title: action.label } : undefined}
        >
          <CircleIcon icon={action.icon} className={styles.mediaBtn} />
        </PrimaryButton>
      );
    });
    return result;
  }, [video, mediaButtons, audio, onChangeVideo, onChangeAudio, topbarActions]);

  const renderDisabedCameraContent = () => {
    if (video) return null;
    return (
      <div className={styles.disabledCameraOverlay}>
        <UserAvatar
          mode="image"
          src={avatar}
          username={userDisplayName}
          className={styles.avatar}
        />
      </div>
    );
  }

  const renderIdleAnimation = () => {
    if (!idleAnimation) return null;
    return (
      <div className={styles.idleAnimationContainer}>
        <FluidCanvas speed={0.5} shapeSize="big" />
      </div>
    );
  };

  const renderLoadingOverlay = () => {
    if (!loading) return null;
    return (
      <div className={styles.loadingOverlay}>
        <ReactIcons.Spinner className={styles.spinner} />
      </div>
    );
  };

  /**
   * Centered round buttons rendered in the overlay slot, on top of any custom
   * `overlayContent`. Same visual language as the topbar buttons but stacked
   * absolutely so they remain centered regardless of layout.
   */
  const renderOverlayActions = () => {
    if (!overlayActions?.length) return null;
    return (
      <div className={styles.overlayActions}>
        {overlayActions.map((action, idx) => (
          <PrimaryButton
            key={action.key ?? `overlay-action-${idx}`}
            onClick={action.onClick}
            nativeProps={action.label ? { "aria-label": action.label, title: action.label } : undefined}
          >
            <CircleIcon icon={action.icon} className={styles.overlayActionCircle} />
          </PrimaryButton>
        ))}
      </div>
    );
  };

  const composedOverlayContent = (overlayContent || overlayActions?.length) ? (
    <>
      {overlayContent}
      {renderOverlayActions()}
    </>
  ) : undefined;

  return (
    <BoxContainer
      className={clsx({
        [styles.container]: true,
        [styles.highlighted]: isHighlighted,
        [styles.playingSound]: isPlayingSound,
        [styles.small]: isSmall,
      }, className)}
      topBarProps={{
        left,
        center: [],
        right: right.length ? right : undefined,
        className: clsx({
          [styles.playerTopBar]: true,
          [styles.transparent]: !video,
        }, topBarClassName),
      }}
      mode={overlayMode}
      overlayContent={composedOverlayContent}
      nativeProps={boxProps}
    >
      {children}
      {renderDisabedCameraContent()}
      {renderIdleAnimation()}
      {renderLoadingOverlay()}
      {localStream && (
        <div style={{
          position: "absolute",
          bottom: 10,
          left: 10,
          color: "white",
          fontSize: "0.75em",
          backgroundColor: "#00000088",
          padding: "2px 5px",
          borderRadius: 5,
        }}>
          local-stream
        </div>
      )}
    </BoxContainer>
  );
});
